import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { isValidPhoneNumber as isValidNumber } from 'libphonenumber-js/min'
import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import React, { useEffect, useRef, useState } from 'react'
import { FieldPath, useForm } from 'react-hook-form'

import ClearPrefillLink from 'src/components/ClearPrefillLink'
import useLogger from 'src/context/LoggerProvider/useLogger'
import {
  useContactPersist,
  useContactPrefill
} from 'src/hooks/useContactPrefill'
import {
  IMutation,
  IMutationSubmitOnDemandLeadArgs,
  ISubmitOnDemandLeadInput,
  OnDemandPersonaType,
  OnDemandPickupLogistics,
  OnDemandPickupTiming,
  OnDemandPickupWindow,
  OnDemandVolumeEstimate,
  StorageTime,
  SubmitOnDemandLeadError
} from 'src/types/graphql'
import { verifyBotId } from 'src/utils/botid'
import { OnDemandCityConfig } from 'src/utils/onDemand'
import posthog from 'src/utils/pixels/posthog'
import { firedOncePerSession } from 'src/utils/pixels/sessionDedup'
import {
  addToCart,
  onDemandLead,
  onDemandLeadStart,
  onDemandWhatsapp
} from 'src/utils/pixels/spotmeEvents'
import { buildWhatsappUrl } from 'src/utils/whatsapp'

import { mapErrorToLocaleKey, normalizePhoneE164 } from './helpers'
import ProgressBar from './ProgressBar'
import {
  StepContact,
  StepLocation,
  StepLogistics,
  StepVolumeAndDuration
} from './steps'
import { IWizardForm, TOTAL_STEPS, WizardStep } from './types'

/**
 * Success is only seen once the lead is submitted, so it's safe to defer
 * its JS off the initial-load chunk. `ssr: false` because it never renders
 * on the server — by the time the user reaches it, hydration is long
 * done.
 */
const Success = dynamic(() => import('./Success'), { ssr: false })

const SUBMIT_ON_DEMAND_LEAD = gql`
  mutation SubmitOnDemandLead($input: SubmitOnDemandLeadInput!) {
    submitOnDemandLead(input: $input) {
      success
      leadId
      error
    }
  }
`

const NAME_MIN_LENGTH = 2

/**
 * Per-step field manifest. RHF's `trigger(fields)` validates only the listed
 * fields when the user clicks "Continuar" — lets each step gate progression
 * to the next without forcing the user to fill fields they haven't seen yet.
 *
 * Order matches the step UI flow:
 *  1) Location — pickup city (the foot-in-the-door). `pickupAddress` is
 *     rendered but optional — see steps.tsx for the rationale (drops the
 *     one mid-step-1 friction point we could attribute to abandonment in
 *     30-day PostHog data; matches /flotillas which collects no address).
 *  2) Volume + Duration — the live-price-reveal step
 *  3) Logistics — pickup timing + logistics + persona type
 *  4) Contact — name + phone (final submit gate)
 */
const STEP_FIELDS: Record<WizardStep, FieldPath<IWizardForm>[]> = {
  1: ['pickupCity'],
  2: ['volumeEstimate', 'storageDuration'],
  3: ['pickupTiming', 'pickupLogistics', 'personaType'],
  4: ['name', 'phone']
}

/**
 * Human-readable slug per wizard step. Used as the `step` param on the
 * `on_demand_wizard_step` PostHog event so funnel insights group cleanly
 * (`location → volume_duration → logistics → contact`) instead of
 * by-numeric-index. Mirrors the wizard step manifest above.
 */
const STEP_SLUGS: Record<WizardStep, string> = {
  1: 'location',
  2: 'volume_duration',
  3: 'logistics',
  4: 'contact'
}

interface IProps {
  /** City-page variant: pre-selects (and visually locks) the pickup city. */
  coverage?: OnDemandCityConfig
  /** Parent-page variant: renders a select with all active cities. */
  activeCities?: OnDemandCityConfig[]
  /** Parent-page geolocation hint. When the user resolves to a covered,
   *  partner-active market upstream (`useUserLocation` →
   *  `resolveOnDemandCityByCoords`), this is the matching city — used as
   *  the wizard's default pickup city instead of the first active city. */
  personalizedCity?: OnDemandCityConfig
}

/**
 * Multi-step lead wizard for Almacenamiento a Domicilio. Replaces the
 * legacy dual-form setup (compact `HeroLeadForm` + standalone
 * `OnDemandLeadForm`) with a single progressive-disclosure flow inspired
 * by Clutter / Youstock and the broader "high-info form" pattern:
 *
 *  1. Location  — pickup city + colonia/dirección (lowest commitment)
 *  2. Volume + Duration — live `PriceReveal` chip rewards each selection
 *  3. Logistics — pickup timing + access + persona type
 *  4. Contact — name + WhatsApp + final submit
 *
 * Why this shape:
 *  - Progressive disclosure ↓ per-step friction → ↑ completion vs a single
 *    long form. Sunk-cost compounds: by step 4 the user has invested 60-90s
 *    of effort and seen a personalized price; bailing costs more than
 *    finishing.
 *  - Easy questions first, ask-questions last. Phone number lands AFTER
 *    the user has been "earned" a real price.
 *  - Single canonical lead surface — no duplicate "small form / big form"
 *    inconsistency; one mutation, one success state.
 *
 * Submit + pixel fan-out preserves the prior behavior so the marketing
 * pipeline (GA4 / Meta / UET ADD_TO_CART with `subType: 'on_demand'` +
 * `onDemandLead`) keeps slicing the Traditional funnel correctly. The
 * `onDemandLeadStart` event still fires on first form interaction —
 * gated by `leadStartFiredRef` so tabbing between fields doesn't
 * re-emit.
 *
 * Honeypot lives on step 4 (the contact step) where a naive bot will
 * fill every visible field. A trip silently renders the success state
 * without calling the mutation, identical to the prior single-form
 * behavior — bots stay unaware they've been detected.
 *
 * WhatsApp escape hatch: surfaced on step 4 as a secondary CTA. On click
 * we silently POST whatever data we have (if a contact-shaped value is
 * present) so the lead still reaches HubSpot, then open WhatsApp with a
 * prefilled message including the city + volume. Mirrors the prior
 * `onWhatsappCta` behavior.
 */
const OnDemandWizard = ({
  coverage,
  activeCities,
  personalizedCity
}: IProps) => {
  const { t } = useTranslation('page_almacenamiento_a_domicilio')
  const logger = useLogger()
  const [step, setStep] = useState<WizardStep>(1)
  const [submittedSilently, setSubmittedSilently] = useState(false)
  const [success, setSuccess] = useState(false)
  const [topLevelError, setTopLevelError] = useState<
    'GENERIC' | 'CITY_NOT_COVERED' | 'RATE_LIMITED' | null
  >(null)

  // Tracks whether `storage_on_demand_lead_start` has fired so we only emit
  // it once per wizard instance. Doubles as the "user has interacted, don't
  // auto-overwrite their city" guard for the geolocation pre-fill effect.
  const leadStartFiredRef = useRef(false)

  // Per-step funnel anchor — fires `on_demand_wizard_step` on first arrival
  // at each step so PostHog can build the on-demand wizard funnel that
  // surfaces mid-funnel drop-off (`lead_start → volume_duration → logistics
  // → contact → lead`). Mirrors how the checkout / KYC layouts tag the
  // session. Carries `step_number` (1–4) + `step_id` (slug). Two-layer
  // dedup: the in-memory ref guards against re-fires within this wizard
  // instance (Strict-Mode double-invoke, back-and-forth nav), and
  // `firedOncePerSession` layers cross-mount session dedup on top so each
  // step counts once per browser session (matches the funnel's
  // unique-per-session read). On storage-disabled browsers the session
  // helper no-ops and the in-memory ref still holds.
  const wizardStepFiredRef = useRef<Set<WizardStep>>(new Set())
  useEffect(() => {
    if (wizardStepFiredRef.current.has(step)) return
    wizardStepFiredRef.current.add(step)
    if (firedOncePerSession(`on_demand_wizard_step:${step}`)) return
    posthog.event('on_demand_wizard_step', {
      step_number: step,
      step_id: STEP_SLUGS[step]
    })
  }, [step])

  // Abandonment funnel — fires `on_demand_wizard_abandoned` on unmount if
  // the user engaged with the wizard (`leadStartFiredRef.current`) but
  // never reached the submit-success state. Funnel drop-off via the
  // per-step event already shows this implicitly, but the explicit event
  // unlocks Meta / Google retargeting of "started wizard but didn't
  // finish" cohorts. Gated on `leadStartFiredRef` so pure bouncers who
  // never focused a field don't generate noise.
  const stepRef = useRef<WizardStep>(step)
  const successRef = useRef<boolean>(success)
  useEffect(() => {
    stepRef.current = step
  }, [step])
  useEffect(() => {
    successRef.current = success
  }, [success])
  useEffect(() => {
    return () => {
      if (!leadStartFiredRef.current) return
      if (successRef.current) return
      posthog.event('on_demand_wizard_abandoned', {
        last_step: STEP_SLUGS[stepRef.current],
        last_step_index: stepRef.current
      })
    }
  }, [])

  // Card resolver — same logic as the legacy forms: explicit coverage on a
  // city page becomes the locked single option; otherwise filter active
  // cities to partner-active.
  const cityOptions = coverage
    ? [coverage]
    : (activeCities ?? []).filter((c) => c.partnerActive)

  // Default city: explicit coverage > geolocation > first active city.
  const defaultCity =
    coverage?.canonicalSlug ??
    personalizedCity?.canonicalSlug ??
    cityOptions[0]?.canonicalSlug ??
    ''

  const {
    handleSubmit,
    control,
    formState: { errors, touchedFields },
    setError,
    setValue,
    getValues,
    watch,
    trigger
  } = useForm<IWizardForm>({
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      pickupCity: defaultCity,
      pickupZone: '',
      pickupAddress: '',
      personaType: '',
      pickupTiming: '',
      storageDuration: '',
      pickupLogistics: '',
      volumeEstimate: OnDemandVolumeEstimate.Unsure,
      notes: '',
      company_url: ''
    },
    // `onTouched` ⇒ errors only appear after a field has been blurred at
    // least once. Critical for the wizard: with `onChange`, the moment the
    // user lands on step 4 (`name` + `phone`), the required-rule
    // controllers register and RHF flags both as invalid on first render,
    // showing red errors before the user has even tabbed into the inputs.
    // `onTouched` keeps inline validation reactive (re-validates on every
    // keystroke after the first blur) without flashing red on arrival.
    mode: 'onTouched'
  })

  // Geolocation pre-fill — once `personalizedCity` resolves after mount,
  // update the pickup city ONLY if the user hasn't started interacting
  // yet. After `leadStartFiredRef` flips, the current value is user-intent.
  useEffect(() => {
    if (coverage) return
    if (!personalizedCity) return
    if (leadStartFiredRef.current) return
    setValue('pickupCity', personalizedCity.canonicalSlug)
  }, [coverage, personalizedCity, setValue])

  // Cross-form contact prefill — same store used by `<LeadForm>`,
  // /flotillas, Traditional, B2B, Apply, Calculate and InviteUser. A
  // renter/host who submitted on any other SpotMe surface lands here with
  // their name/phone/email already in. Same `leadStartFiredRef` guard as
  // the geolocation effect so we never overwrite user typing.
  const { prefill, hasHydrated, source, clearAnonPrefill } = useContactPrefill()
  const persistContact = useContactPersist()
  const prefillInjectedRef = useRef(false)
  useEffect(() => {
    if (!hasHydrated || prefillInjectedRef.current) return
    if (leadStartFiredRef.current) return
    if (!prefill.name && !prefill.phone && !prefill.email) return
    prefillInjectedRef.current = true
    if (prefill.name) setValue('name', prefill.name)
    if (prefill.phone) setValue('phone', prefill.phone)
    if (prefill.email) setValue('email', prefill.email)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHydrated])

  const handleClearPrefill = () => {
    clearAnonPrefill()
    setValue('name', '')
    setValue('phone', '')
    setValue('email', '')
    prefillInjectedRef.current = true
  }

  const [submit, { loading }] = useMutation<
    Pick<IMutation, 'submitOnDemandLead'>,
    IMutationSubmitOnDemandLeadArgs
  >(SUBMIT_ON_DEMAND_LEAD)

  const watchedCity = watch('pickupCity')
  const watchedVolume = watch('volumeEstimate')
  const watchedDuration = watch('storageDuration')

  // Zone options for Step 1 — derived from the partner's `coverageZones`
  // for the resolved pickup city. Replaces the legacy free-text address
  // field that PostHog showed was killing ~86% of users on Step 1.
  // Recomputes when the user changes the city in the select.
  const resolvedCityForZones =
    coverage ??
    cityOptions.find((c) => c.canonicalSlug === watchedCity) ??
    personalizedCity ??
    cityOptions[0]
  const zoneOptions = resolvedCityForZones?.coverageZones ?? []

  // Reset `pickupZone` whenever the resolved city changes — a zone picked
  // on a previous city is no longer valid. Skipped during initial mount
  // (no prior value to clear) and after the user has engaged with the
  // wizard (their explicit input takes precedence).
  const previousResolvedCityRef = useRef(resolvedCityForZones?.canonicalSlug)
  useEffect(() => {
    const current = resolvedCityForZones?.canonicalSlug
    const previous = previousResolvedCityRef.current
    previousResolvedCityRef.current = current
    if (previous === undefined) return
    if (previous === current) return
    setValue('pickupZone', '')
  }, [resolvedCityForZones, setValue])

  // Resolve the per-m² rates for the price reveal — prefer the resolved
  // city, falling back to whichever active city we'd default-select on
  // the parent page. The helper accepts undefined and falls back to
  // partner-published defaults if nothing is available yet.
  const resolvedCityForPricing =
    coverage ??
    cityOptions.find((c) => c.canonicalSlug === watchedCity) ??
    personalizedCity ??
    cityOptions[0]
  const perSqmRates = resolvedCityForPricing?.perSqmRates

  const fireOnDemandPixel = (
    resolvedCity: string,
    storageDuration?: StorageTime | ''
  ) => {
    addToCart({
      subEvent: 'on_demand',
      location: undefined,
      config: undefined,
      spaceType: 'self_storage',
      city: resolvedCity,
      pillar: 'storage-ondemand',
      storageTime: storageDuration
    })
    onDemandLead({ city: resolvedCity })
  }

  // Capture-phase onFocus on the form catches the first interaction with
  // ANY child input without wiring a handler on each.
  const handleFormFocus = () => {
    if (leadStartFiredRef.current) return
    leadStartFiredRef.current = true
    onDemandLeadStart({
      city: coverage?.canonicalSlug ?? cityOptions[0]?.canonicalSlug
    })
  }

  const callMutation = async (formValues: IWizardForm) => {
    // Soft-flag BotID — bounded wait so the verdict can ride the mutation
    // (server keeps the HubSpot lead but skips the market-PARTNER
    // notification for flagged sessions). Covers BOTH the regular submit
    // and the WhatsApp escape-hatch (`onWhatsappCta`) since both funnel
    // through this single mutation call. On timeout we submit unflagged
    // (fail-open, same as the old fire-and-forget); the helper still logs
    // the late verdict to PostHog. See docs/marketing/botid.md.
    const botIdVerdict = await Promise.race([
      verifyBotId({ surface: 'ondemand_wizard' }),
      new Promise<undefined>((resolve) =>
        setTimeout(() => resolve(undefined), 2500)
      )
    ])

    const input: ISubmitOnDemandLeadInput = {
      botIdFlagged: botIdVerdict?.isBot,
      name: formValues.name.trim(),
      phone: normalizePhoneE164(formValues.phone),
      email: formValues.email?.trim() || undefined,
      pickupCity: formValues.pickupCity,
      // Step 1's free-text address was replaced by a `pickupZone` dropdown
      // sourced from the partner's `coverageZones` (PostHog showed 86%
      // abandonment on the old field). Send the zone through the existing
      // `pickupAddress` mutation field so HubSpot + the backend stay
      // compatible without a schema change — sales sees the zone name in
      // the deal record. Falls back to legacy `pickupAddress` (empty unless
      // a future surface re-introduces a freeform field).
      pickupAddress:
        formValues.pickupZone?.trim() ||
        formValues.pickupAddress?.trim() ||
        undefined,
      personaType: (formValues.personaType as OnDemandPersonaType) || undefined,
      pickupTiming:
        (formValues.pickupTiming as OnDemandPickupTiming) || undefined,
      storageDuration: (formValues.storageDuration as StorageTime) || undefined,
      pickupLogistics:
        (formValues.pickupLogistics as OnDemandPickupLogistics) || undefined,
      volumeEstimate: formValues.volumeEstimate,
      pickupWindow: OnDemandPickupWindow.OneOff,
      notes: formValues.notes?.trim() || undefined,
      honeypot: undefined
    }

    try {
      const { data } = await submit({ variables: { input } })
      const result = data?.submitOnDemandLead
      if (result?.success) {
        return { handled: true as const, leadId: result.leadId }
      }
      const errorKey = mapErrorToLocaleKey(result?.error)
      if (result?.error === SubmitOnDemandLeadError.InvalidPhone) {
        setError('phone', { message: t('FORM.VALIDATION.PHONE_INVALID') })
      } else if (errorKey) {
        setTopLevelError(errorKey)
      }
      return { handled: false as const }
    } catch (err: unknown) {
      logger.error('on_demand_wizard_submit_failed', {
        message: err instanceof Error ? err.message : 'unknown'
      })
      setTopLevelError('GENERIC')
      return { handled: false as const }
    }
  }

  const onSubmit = async (formValues: IWizardForm) => {
    setTopLevelError(null)

    // Honeypot tripped — silently render success-state without calling the
    // mutation. Bots don't learn they've been detected.
    if (formValues.company_url?.trim()) {
      setSubmittedSilently(true)
      setSuccess(true)
      return
    }

    if (formValues.name.trim().length < NAME_MIN_LENGTH) {
      setError('name', { message: t('FORM.VALIDATION.NAME_TOO_SHORT') })
      return
    }
    if (!isValidNumber(normalizePhoneE164(formValues.phone))) {
      setError('phone', { message: t('FORM.VALIDATION.PHONE_INVALID') })
      return
    }

    const result = await callMutation(formValues)
    if (result.handled) {
      fireOnDemandPixel(
        formValues.pickupCity,
        (formValues.storageDuration as StorageTime) || ''
      )
      persistContact({
        name: formValues.name,
        phone: formValues.phone,
        email: formValues.email
      })
      setSuccess(true)
    }
  }

  // Secondary CTA: silently POST whatever the user has filled (so the lead
  // reaches HubSpot first) then open WhatsApp with a prefilled message.
  // Mirrors the legacy form's behavior.
  const onWhatsappCta = async () => {
    setTopLevelError(null)
    onDemandWhatsapp({
      city: coverage?.canonicalSlug ?? cityOptions[0]?.canonicalSlug
    })
    const values = getValues()
    let leadAccepted = false

    if (!values.company_url?.trim()) {
      const ready =
        values.name.trim().length >= NAME_MIN_LENGTH &&
        !!values.phone &&
        isValidNumber(normalizePhoneE164(values.phone))
      if (ready) {
        const result = await callMutation(values)
        leadAccepted = result.handled
        if (leadAccepted) {
          fireOnDemandPixel(
            values.pickupCity,
            (values.storageDuration as StorageTime) || ''
          )
          persistContact({
            name: values.name,
            phone: values.phone,
            email: values.email
          })
        }
      }
    }

    const cityLabel =
      cityOptions.find((c) => c.canonicalSlug === values.pickupCity)
        ?.displayName ?? values.pickupCity
    const volumeKey = values.volumeEstimate
    const volumeLabel = t(
      `FORM.FIELDS.VOLUME.OPTIONS.${
        volumeKey === OnDemandVolumeEstimate.Small
          ? 'SMALL'
          : volumeKey === OnDemandVolumeEstimate.Medium
            ? 'MEDIUM'
            : volumeKey === OnDemandVolumeEstimate.Large
              ? 'LARGE'
              : 'UNSURE'
      }` as 'FORM.FIELDS.VOLUME.OPTIONS.SMALL'
    )
    const message = `Hola SpotMe, quiero cotizar Almacenamiento a Domicilio. Ciudad: ${cityLabel}. Volumen: ${volumeLabel}.`
    const url = buildWhatsappUrl(message)

    if (typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener,noreferrer')
    }

    if (leadAccepted) setSuccess(true)
  }

  // Advance to the next step IFF the current step's fields validate.
  // Falls back to a no-op when validation fails — RHF surfaces the field
  // errors inline.
  const handleNext = async () => {
    const valid = await trigger(STEP_FIELDS[step])
    if (!valid) return
    if (step < TOTAL_STEPS) setStep(((step as number) + 1) as WizardStep)
  }

  const handleBack = () => {
    if (step > 1) setStep(((step as number) - 1) as WizardStep)
  }

  if (success) {
    // Same derivation the step-4 WhatsApp escape hatch uses for its own
    // prefill — the two doors out of this wizard must hand the chatbot the
    // same city, or the post-submit one opens a thread with no city at all.
    const submittedCityLabel =
      cityOptions.find((c) => c.canonicalSlug === watchedCity)?.displayName ??
      resolvedCityForZones?.displayName ??
      ''
    return (
      <Success
        submittedSilently={submittedSilently}
        cityLabel={submittedCityLabel}
      />
    )
  }

  const stepTitle = t(`WIZARD.STEP_${step}.TITLE` as 'WIZARD.STEP_1.TITLE')
  const stepSubhead = t(
    `WIZARD.STEP_${step}.SUBHEAD` as 'WIZARD.STEP_1.SUBHEAD'
  )

  return (
    <div
      className="border border-ink-100/15 bg-white-500 p-6 md:p-7"
      id="cotizar"
    >
      <ProgressBar step={step} />

      <h3 className="mt-2 font-display text-[22px] leading-tight font-medium text-ink-700 md:text-[24px]">
        {stepTitle}
      </h3>
      <p className="mt-2 text-[13px] leading-relaxed text-ink-200">
        {stepSubhead}
      </p>

      {/* Mobile: reserve the cookie slip's height (`--consent-h`, published
          by CookieConsent while it is on screen) so «Continuar» can always
          scroll clear of it. Static class — SSR and client markup match. */}
      <form
        onSubmit={handleSubmit(onSubmit)}
        onFocus={handleFormFocus}
        noValidate
        className="mt-5 pb-[var(--consent-h,0px)] md:pb-0"
      >
        {step === 1 && (
          <StepLocation
            control={control}
            errors={errors}
            coverage={coverage}
            cityOptions={cityOptions}
            watchedCity={watchedCity}
            zoneOptions={zoneOptions}
          />
        )}
        {step === 2 && (
          <StepVolumeAndDuration
            control={control}
            errors={errors}
            watchedVolume={watchedVolume}
            watchedDuration={watchedDuration}
            perSqmRates={perSqmRates}
          />
        )}
        {step === 3 && <StepLogistics control={control} errors={errors} />}
        {step === 4 && (
          <StepContact
            control={control}
            errors={errors}
            touchedFields={touchedFields}
          />
        )}

        {topLevelError ? (
          <div
            role="alert"
            className="mt-4 rounded-md border border-red-200 bg-red-100 px-3 py-2 text-[12px] leading-relaxed text-red-700"
          >
            {t(`FORM.ERROR.${topLevelError}`)}
          </div>
        ) : null}

        <div className="mt-6 flex flex-col gap-3">
          {step < TOTAL_STEPS ? (
            <button
              type="button"
              onClick={handleNext}
              className="w-full rounded-full bg-blue-500 px-7 py-3.5 text-center text-[15px] font-semibold text-white-500 shadow-[0_20px_40px_-16px_rgba(0,164,255,0.55)] transition-all duration-200 hover:bg-blue-600 active:scale-[0.98]"
            >
              {t('WIZARD.NEXT_CTA')}
            </button>
          ) : (
            <button
              disabled={loading}
              aria-busy={loading}
              className="w-full rounded-full bg-blue-500 px-7 py-3.5 text-center text-[15px] font-semibold text-white-500 shadow-[0_20px_40px_-16px_rgba(0,164,255,0.55)] transition-all duration-200 hover:bg-blue-600 active:scale-[0.98] disabled:opacity-60"
            >
              {loading ? t('FORM.SUBMITTING') : t('WIZARD.SUBMIT_CTA')}
            </button>
          )}

          <div className="flex flex-col items-center gap-3">
            {step > 1 && (
              <button
                type="button"
                onClick={handleBack}
                disabled={loading}
                className="inline-flex items-center gap-1.5 text-[13px] font-medium text-ink-200 transition-colors hover:text-blue-500 disabled:opacity-60"
              >
                <i
                  className="si si-arrow-left text-[10px]"
                  aria-hidden="true"
                />
                {t('WIZARD.BACK_CTA')}
              </button>
            )}

            {step === TOTAL_STEPS && (
              <button
                type="button"
                onClick={onWhatsappCta}
                disabled={loading}
                className="inline-flex items-center gap-2 text-[13px] font-medium text-ink-700 transition-colors hover:text-blue-500 disabled:opacity-60"
              >
                <i
                  className="si si-chat text-[12px] text-blue-500"
                  aria-hidden="true"
                />
                <span className="underline decoration-blue-500/40 underline-offset-4">
                  {t('FORM.WHATSAPP_SECONDARY')}
                </span>
              </button>
            )}

            {/* "¿No eres tú?" — only renders when prefill came from the
                anon localStorage store. Lets a shared-device handoff start
                a fresh wizard without manually clearing each field. */}
            {step === TOTAL_STEPS && (
              <ClearPrefillLink
                source={source}
                onClear={handleClearPrefill}
                className="text-[11px] text-ink-200 underline-offset-4 hover:text-blue-500 hover:underline"
              />
            )}
          </div>
        </div>
      </form>
    </div>
  )
}

export default React.memo(OnDemandWizard)
